import { ApiMeEmployee } from "@/types/employees/employee.types.ts";
import ReactGA from "react-ga4";

export const analyticEvents = {
  loggedIn: "Logged in",
  passwordChanged: "Password Changed",
  passwordReset: "Password Reset",
  inviteAccepted: "Invite Accepted",
  pageAction: "Page Action",
  dataExported: "Data Exported",
  planInactiveViewed: "Plan Inactive Viewed",
  serverErrorViewed: "Server Error Viewed",
  settings: {
    viewed: "Settings Viewed",
  },
  organization: {
    settingsViewed: "Organization Settings Viewed",
    settingsEdited: "Organization Settings Edited",
  },
  divisions: {
    viewed: "Divisions List Viewed",
    created: "Division Created",
    edited: "Division Edited",
    deleted: "Division Deleted",
  },
  departments: {
    viewed: "Departments List Viewed",
    viewedChart: "Departments Chart Viewed",
    created: "Department Created",
    edited: "Department Edited",
    deleted: "Department Deleted",
  },
  roles: {
    viewed: "Roles List Viewed",
    viewedEmployees: "Employees List With Roles Viewed",
    created: "Role Created",
    edited: "Role Edited",
    deleted: "Role Deleted",
  },
  timeOffs: {
    categoriesViewed: "Time Off Categories List Viewed",
    created: "Time Off Category Created",
    edited: "Time Off Category Edited",
    deleted: "Time Off Category Deleted",
    createdPolicy: "Time Off Policy Created",
    editedPolicy: "Time Off Policy Edited",
    duplicatedPolicy: "Time Off Policy Duplicated",
    balanceAdjusted: "Time Off Balance Adjusted",
    // calendarFiltered: "Time Off Calendar Filtered",
    requestViewed: "Time Off Request Viewed",
    categoriesSorted: "Time Off Categories Sorted",
    calendarViewed: "Time Off Calendar Viewed",
  },
  approvals: {
    categoryViewed: "Approval Category Policies List Viewed",
    categoriesViewed: "Approval Categories List Viewed",
    historyViewed: "Approval Requests History Viewed",
    categoryCreated: "Approval Category Created",
    categoryEdited: "Approval Category Edited",
    categoryDeleted: "Approval Category Deleted",
    createdPolicy: "Approval Policy Created",
    editedPolicy: "Approval Policy Edited",
    historyFiltered: "Approval Requests History Filtered",
    requestCancelled: "Approval Request Cancelled",
    requestApprovalViewed: "Approval Request Viewed",
    requestCreated: "Approval Request Created",
    requestEdited: "Approval Request Edited",
    requestAction: "Approval Request Action",
  },
  employees: {
    viewed: "Employees List Viewed",
    previewViewed: "Employees Preview List Viewed",
    created: "Employee Created",
    edited: "Employee Edited",
    dismissed: "Employee Terminated",
    // dismissalDeleted: "Employee Termination Deleted",
    viewedEmployee: "Employee Viewed",
    viewedEmployeeWorkInfoHistory: "Employee Work Info History Viewed",
    viewedChart: "Employee Chart Viewed",
    accountSettingsViewed: "Employee Account Settings Viewed",
    accountSettingsEdited: "Employee Account Settings Edited",
    timeOffHistoryViewed: "Employee Time Off History Viewed",
    filtered: "Employees Filtered",
    settingsEdited: "Employees Settings Edited",
    deleted: "Employee Deleted",
    banned: "Employee Access Disabled",
    unbanned: "Employee Access Enabled",
  },
  employeePositions: {
    created: "Employee Position Created",
    edited: "Employee Position Edited",
    deleted: "Employee Position Deleted",
  },
  employeeCompensations: {
    created: "Employee Compensation Created",
    edited: "Employee Compensation Edited",
    deleted: "Employee Compensation Deleted",

    additionalCreated: "Employee Additional Compensation Created",
    additionalEdited: "Employee Additional Compensation Edited",
    additionalDeleted: "Employee Additional Compensation Deleted",
  },
  employeeEmployments: {
    created: "Employee Employment Created",
    edited: "Employee Employment Edited",
    deleted: "Employee Employment Deleted",
  },
  pipelines: {
    viewed: "Pipelines List Viewed",
    created: "Pipeline Created",
    edited: "Pipeline Edited",
    deleted: "Pipeline Deleted",
    stageCreated: "Pipeline Stage Created",
    stageEdited: "Pipeline Stage Edited",
    stageDeleted: "Pipeline Stage Deleted",
    stagesSorted: "Pipeline Stages Sorted",
  },
  sources: {
    viewed: "Sources List Viewed",
    created: "Source Created",
    edited: "Source Edited",
    deleted: "Source Deleted",
  },
  labels: {
    viewed: "Labels List Viewed",
    created: "Label Created",
    edited: "Label Edited",
    deleted: "Label Deleted",
  },
  disqualificationReasons: {
    viewed: "Disqualification Reasons List Viewed",
    created: "Disqualification Reason Created",
    edited: "Disqualification Reason Edited",
    deleted: "Disqualification Reason Deleted",
  },
  jobArchivingReasons: {
    viewed: "Job Archiving Reasons List Viewed",
    created: "Job Archiving Reason Created",
    edited: "Job Archiving Reason Edited",
    deleted: "Job Archiving Reason Deleted",
  },
  jobs: {
    viewed: "Jobs List Viewed",
    created: "Job Created",
    edited: "Job Edited",
    duplicated: "Job Duplicated",
    detailsViewed: "Job Details Viewed",
  },
  applicants: {
    viewed: "Applicants List Viewed",
    created: "Applicant Created",
    edited: "Applicant Edited",
    viewedApplicant: "Applicant Viewed",
    assignedToJob: "Applicant Assigned To Job",
    addedAction: "Applicant Added Action",
    actionEdited: "Applicant Action Edited",
    actionDeleted: "Applicant Action Deleted",
    cvViewed: "Applicant CV Viewed",
    cvDownloaded: "Applicant CV Downloaded",
    cvTextEdited: "Applicant CV Text Edited",
    deleted: "Applicant Deleted",
    labelsEdited: "Applicant Labels Edited",
    mergeResolved: "Applicant Merge Resolved",
    filtered: "Applicants Filtered",
  },
  candidates: {
    addedAction: "Candidate Added Action",
    changedStage: "Candidate Changed Stage",
    disqualified: "Candidate Disqualified",
    qualified: "Candidate Qualified",
    deleted: "Candidate Deleted",
    actionDeleted: "Candidate Action Deleted",
    actionEdited: "Candidate Action Edited",
    snoozed: "Candidate Snoozed",
  },
  reporting: {
    viewedOverview: "Reporting Viewed",
    viewed: "Report Viewed",
    dataViewed: "Report Data Viewed",
    filtered: "Report Filtered",
  },
  email: {
    sent: "Email Sent",
    disconnected: "Email Disconnected",
    viewed: "Email Viewed",
  },
  calendar: {
    disconnected: "Calendar Disconnected",
  },
  events: {
    calendarViewed: "Calendar Viewed",
    created: "Event Created",
    edited: "Event Edited",
    cancelled: "Event Cancelled",
    viewed: "Event Viewed",
    deleted: "Event Deleted",
    joinedConference: "Event Joined Conference",
    responded: "Event Responded",
  },
  documents: {
    folderCreated: "Documents Folder Created",
    folderEdited: "Documents Folder Edited",
    folderDeleted: "Documents Folder Deleted",
    folderRolesModalViewed: "Documents Folder Roles Modal Viewed",
    viewed: "Document Viewed",
    downloaded: "Document Downloaded",
    created: "Document Created",
    edited: "Document Edited",
    moved: "Document Moved",
    deleted: "Document Deleted",
  },
  esign: {
    markupSaved: "Esign Markup Saved",
    requestSent: "Esign Request Sent",
    signed: "Esign Request Signed",
    acknowledged: "Esign Request Acknowledged",
    identityVerified: "Esign Identity Verified",
  },
  locations: {
    viewed: "Locations List Viewed",
    created: "Location Created",
    edited: "Location Edited",
    deleted: "Location Deleted",
  },
  legalEntities: {
    viewed: "Legal Entities List Viewed",
    created: "Legal Entity Created",
    edited: "Legal Entity Edited",
    deleted: "Legal Entity Deleted",
  },
  employeeDismissalReasons: {
    viewed: "Termination Reasons List Viewed",
    edited: "Termination Reason Edited",
  },
  employeeTerminationLeaveTypes: {
    viewed: "Termination Leave Types List Viewed",
    edited: "Termination Leave Type Edited",
  },
  templates: {
    viewed: "Templates List Viewed",
    created: "Template Created",
    edited: "Template Edited",
    deleted: "Template Deleted",
    viewedChooseTemplate: "Template Choose Viewed",
    copied: "Template Copied",
  },
  home: {
    viewed: "Viewed Home",
    layoutSaved: "Home Layout Saved",
  },
  dataProtection: {
    viewed: "Data Protection Viewed",
    settingsEdited: "Data Protection Settings Edited",
    settingsToggled: "Data Protection Settings Toggled",
    requestForOrganizationSent: "Data Protection Request For Organization Sent",
    requestSent: "Data Protection Request Sent",
    responsePageViewed: "Data Protection Response Page Viewed",
    responseReceived: "Data Protection Response Received",
  },
  integrations: {
    viewed: "Integrations Viewed",
    manageViewed: "Integration Manage Viewed",
    edited: "Integration Edited",
    connectStarted: "Integration Connect Started",
    disconnected: "Integration Disconnected",
  },
  pageActions: {
    newFeatureTooltipViewed: "New Feature Tooltip Viewed",
    newVersionUpdateClicked: "New Version Update Clicked",
  },
  calendarHolidays: {
    viewed: "Calendar Holidays List Viewed",
    created: "Calendar Holiday Created",
    viewedCalendarHoliday: "Calendar Holiday Viewed",
    edited: "Calendar Holiday Edited",
    deleted: "Calendar Holiday Deleted",
    holidayCreated: "Calendar Holiday Date Created",
    holidayEdited: "Calendar Holiday Date Edited",
    holidayDeleted: "Calendar Holiday Date Deleted",
    holidayCopied: "Calendar Holiday Dates Copied",
  },
  app: {
    newVersionAvailable: "App New Version Available",
  },
  mentions: {
    viewed: "Mentions Viewed",
  },
  team: {
    teamCreated: "Team Created",
    teamEdited: "Team Edited",
    teamDeleted: "Team Deleted",
    teamViewed: "Team Viewed",
  },
  employmentTypes: {
    viewed: "Employment Types List Viewed",
    created: "Employment Type Created",
    edited: "Employment Type Edited",
    deleted: "Employment Type Deleted",
  },
  employmentContracts: {
    viewed: "Employment Contracts List Viewed",
    created: "Employment Contracts Created",
    edited: "Employment Contracts Edited",
    deleted: "Employment Contracts Deleted",
  },
  hiringPlan: {
    viewed: "Hiring Plans List Viewed",
    requestCreated: "Request Position Created",
    requestEdited: "Request Position Edited",
    requestViewed: "Request Position Viewed",
    requestAction: "Request Position Action",
    filtered: "Hiring Plan Filtered",
  },
  offers: {
    viewed: "Offers List Viewed",
    requestCreated: "Offer Request Created",
    requestAction: "Offer Request Action",
    requestViewed: "Offer Request Viewed",
    requestEdited: "Offer Request Edited",
    offerDownloaded: "Offer Downloaded",
  },
  notificationsSetting: {
    viewed: "Notifications Setting Viewed",
    edited: "Notifications Setting Edited",
  },
  surveys: {
    viewed: "Surveys List Viewed",
    viewedSurvey: "Survey Viewed",
    launched: "Survey Launched",
    paused: "Survey Paused",
    completed: "Survey Completed",
    created: "Survey Created",
    edited: "Survey Edited",
    deleted: "Survey Deleted",
    duplicated: "Survey Duplicated",
    reopened: "Survey Reopened",
    responseFormViewed: "Survey Response Form Viewed",
    responseStarted: "Survey Response Started",
    responseDraftSaved: "Survey Response Draft Saved",
    responseCompleted: "Survey Response Completed",
    surveyResultsDownloaded: "Survey Results Downloaded",
    surveyResultsExported: "Survey Results Exported",
    participantResultViewed: "Survey Participant Result Viewed",
    questionResultViewed: "Survey Question Result Viewed",
    startedFromScratch: "Started From Scratch",
  },
  scoreCards: {
    viewed: "Scorecards List Viewed",
    created: "Scorecards Created",
    duplicated: "Scorecards Duplicated",
    deleted: "Scorecards Deleted",
    edited: "Scorecards Edited",
    published: "Scorecards Published",
    feedbackStarted: "Scorecards Feedback Started",
    feedbackCompleted: "Scorecards Feedback Completed",
    invitesSent: "Scorecards Invites For Feedback Sent",
    resultsViewed: "Scorecards Feedback Results Viewed",
    feedbacksViewed: "Scorecards Feedbacks Viewed",
    archiveFeedback: "Scorecards Feedback Archived",
    feedbackDownloaded: "feedback Downloaded",
  },
  blog: {
    channelViewed: "Channel Viewed",
    channelCreated: "Channel Created",
    channelUpdated: "Channel Updated",
    channelDeleted: "Channel Deleted",
    postViewed: "Post Viewed",
    postCreated: "Post Created",
    postUpdated: "Post Updated",
    postDeleted: "Post Deleted",
    commentCreated: "Post Comment Created",
    commentUpdated: "Post Comment Updated",
    commentDeleted: "Post Comment Deleted",
    reactionAdded: "Post Reaction Added",
    reactionDeleted: "Post Reaction Deleted",
  },
  surveyTemplates: {
    viewed: "Survey Templates List Viewed",
    created: "Survey Template Created",
    duplicated: "Survey Template Duplicated",
    edited: "Survey Template Edited",
    deleted: "Survey Template Deleted",
    createdFromSurvey: "Survey Template Created From Survey",
  },
  formScales: {
    viewed: "Form Scales List Viewed",
    created: "Form Scale Created",
    edited: "Form Scale Edited",
    deleted: "Form Scale Deleted",
  },
  performanceReviews: {
    viewed: "Performance Reviews List Viewed",
    viewedReview: "Performance Review Viewed",
    created: "Performance Review Created",
    edited: "Performance Review Edited",
    deleted: "Performance Review Deleted",
    launched: "Performance Review Launched",
    paused: "Performance Review Paused",
    completed: "Performance Review Completed",
    reopened: "Performance Review Reopened",
    reviewerResultViewed: "Reviewer Result Viewed",

    responseFormViewed: "Performance Review Response Form Viewed",
    responseStarted: "Performance Review Response Started",
    responseDraftSaved: "Performance Review Response Draft Saved",
    responseCompleted: "Performance Review Response Completed",

    resultsExported: "Performance Review Results Exported",
  },
  performanceReviewsTemplates: {
    viewed: "Performance Reviews Templates List Viewed",
    created: "Performance Reviews Template Created",
    duplicated: "Performance Reviews Template Duplicated",
    edited: "Performance Reviews Template Edited",
    deleted: "Performance Reviews Template Deleted",
    createdFrom: "Performance Reviews Template Created From Performance Reviews",
  },
  help: {
    viewed: "Help Center Viewed",
    collectionViewed: "Help Collection Viewed",
    articleViewed: "Help Article Viewed",
  },
  careerSites: {
    viewed: "Career Sites Viewed",
    created: "Career Sites Created",
    edited: "Career Sites Edited",
    deleted: "Career Sites Deleted",
  },
  compensationChangeReasons: {
    viewed: "Compensation Change Reasons List Viewed",
    created: "Compensation Change Reason Created",
    edited: "Compensation Change Reason Edited",
    deleted: "Compensation Change Reason Deleted",
  },
  customFields: {
    sortingViewed: "Custom Fields Sorting Viewed",
    sectionCreated: "Custom Fields Section Created",
    sectionEdited: "Custom Fields Section Edited",
    sectionDeleted: "Custom Fields Section Deleted",
    tableCreated: "Custom Fields Table Created",
    tableEdited: "Custom Fields Table Edited",
    tableDeleted: "Custom Fields Table Deleted",
    fieldCreated: "Custom Fields Field Created",
    fieldEdited: "Custom Fields Field Edited",
    fieldDeleted: "Custom Fields Field Deleted",
    optionCreated: "Custom Fields Option Created",
    optionEdited: "Custom Fields Option Edited",
    optionDeleted: "Custom Fields Option Deleted",
    rolesModalViewed: "Custom Fields Roles Modal Viewed",
  },
  compensationTypes: {
    viewed: "Compensation Types List Viewed",
    created: "Compensation Type Created",
    edited: "Compensation Type Edited",
    archived: "Compensation Type Archived",
    sorted: "Compensation Types Sorted",
  },
  employeePositionChangeReasons: {
    viewed: "Employee Position Change Reasons List Viewed",
    edited: "Employee Position Change Reason Edited",
  },
  probationPolicies: {
    viewed: "Probation Policies List Viewed",
    created: "Probation Policy Created",
    edited: "Probation Policy Edited",
    deleted: "Probation Policy Deleted",
  },
  workPatterns: {
    viewed: "Work Patterns List Viewed",
    created: "Work Pattern Created",
    edited: "Work Pattern Edited",
    deleted: "Work Pattern Deleted",
  },
  workflows: {
    viewed: "Workflows List Viewed",
    overviewViewed: "Workflows Overview Viewed",

    created: "Workflow Created",
    edited: "Workflow Edited",
    deleted: "Workflow Deleted",
    duplicated: "Workflow Duplicated",

    activated: "Workflow Activated",
    deactivated: "Workflow Deactivated",
    // paused: "Workflow Paused",

    // detailsViewed: "Workflow Details Viewed",
    // automationViewed: "Workflow Automation Viewed",
    // automationSetupViewed: "Workflow Automation Setup Viewed",
    // flowViewed: "Workflow Flow Viewed",
    // finalViewed: "Workflow Final Page Viewed",

    blockCreated: "Workflow Block Created",
    blockEdited: "Workflow Block Edited",
    blockDeleted: "Workflow Block Deleted",
    // notificationConfigured: "Workflow Notification Configured",
    assignedToEmployee: "Workflow Assigned To Employee",

    // executionViewed: "Workflow Execution Viewed",
    // executionsFiltered: "Workflow Executions Filtered",

    // exported: "Workflows Exported",
  },
  twoFactorAuthentication: {
    enabled: "Two Factor Enabled",
    disabled: "Two Factor Disabled",
    settingsEnabled: "Two Factor Settings Enabled",
    settingsDisabled: "Two Factor Settings Disabled",
  },
  goals: {
    listShown: "List Shown",
    employeeListShown: "Employee List Shown",
    goalCreated: "Goal Created",
    goalEdited: "Goal Edited",
    goalDeleted: "Goal Deleted",
    metricsShown: "Metrics Shown",
    metricCreated: "Metric Created",
    metricEdited: "Metric Edited",
    metricDeleted: "Metric Deleted",
  },
  tasks: {
    viewed: "Tasks Viewed",
    taskViewed: "Task Viewed",
    created: "Task Created",
    edited: "Task Edited",
    completed: "Task Completed",
    uncompleted: "Task Uncompleted",
    started: "Task Started",
    stopped: "Task Stopped",
    archived: "Task Archived",
  },
  organizationLinks: {
    viewed: "Organization Links List Viewed",
    created: "Organization Link Created",
    edited: "Organization Link Edited",
    deleted: "Organization Link Deleted",
  },
  reminders: {
    historyViewed: "Reminder History Viewed",
    sent: "Reminder History Sent",
  },
  timeTracking: {
    timesheetViewed: "Time Tracking Timesheet Viewed",
    clockedIn: "Time Tracking Clocked In",
    clockedOut: "Time Tracking Clocked Out",
    entryCreated: "Time Tracking Entry Created",
    entryEdited: "Time Tracking Entry Edited",
    entryDeleted: "Time Tracking Entry Deleted",
  },
  jobCatalog: {
    frameworkViewed: "Job Catalog Framework Viewed",
    frameworkEdited: "Job Catalog Framework Edited",
    roleViewed: "Job Catalog Role Viewed",
    roleCreated: "Job Catalog Role Created",
    roleEdited: "Job Catalog Role Edited",
    profileEdited: "Job Catalog Profile Edited",
  },
};

export const analyticProperties = {
  id: "Id",
  url: "URL",
  source: "Source",
  tab: "Tab",
  actionType: "Action Type",
  title: "Title",
  pipelineStageTitle: "Stage Title",
  reasonTitle: "Reason Title",
  format: "Format",
  count: "Count",
  reserved: "Reserved",
  mode: "Mode",
};

class AnalyticsService {
  googleAnalyticsKey: string | undefined;

  constructor(googleAnalyticsKey: string | undefined) {
    this.googleAnalyticsKey = googleAnalyticsKey;
    this.init();
  }

  public async init() {
    if (this.googleAnalyticsKey) {
      ReactGA.initialize(this.googleAnalyticsKey);
    }
  }

  public setUser(user: ApiMeEmployee) {
    if (this.googleAnalyticsKey) {
      ReactGA.gtag("set", "user_properties", {
        email: user.email,
        name: `${user.first_name} ${user.last_name}`,
        organization_id: user.organization_id,
        user_id: user.id.toString(),
      });
    }
  }

  public trackEvent(
    eventName: string,
    properties: Record<string, string | number | boolean | undefined> = {}
  ) {
    if (this.googleAnalyticsKey) {
      ReactGA.event(eventName, {
        ...properties,
        url: window.location.href,
      });
    }
  }
}

const analyticsService = new AnalyticsService(import.meta.env.VITE_GOOGLE_ANALYTICS_KEY);
export default analyticsService;
